// RGM Studio Pro · Vocal FX — real processing (see lib/studioFx.js).
// Changes are heard instantly; saved per track; original takes untouched.
import React from "react";
import { X, Lock, Crown, Power, Ban, Mic2, Waves, Heart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Slider } from "../ui/slider";
import { FX_PRESETS, normalizeFx, fxActive, gateActive } from "../../lib/studioFx";

// RGM preset gallery artwork (small local webp files, served with the app).
const PRESET_ART = {
  rap_presence: { name: "Spotlight", img: "/studio-fx/rap_presence.webp" },
  melodic: { name: "Velvet Sky", img: "/studio-fx/melodic.webp" },
  rnb: { name: "Midnight Silk", img: "/studio-fx/rnb.webp" },
  adlib_wide: { name: "Wide Echo", img: "/studio-fx/adlib_wide.webp" },
};
const PRESET_ICONS = {
  none: { Icon: Ban, cls: "text-gray-400 bg-white/5" },
  rap_presence: { Icon: Mic2, cls: "text-orange-200 bg-orange-500/20" },
  melodic: { Icon: Waves, cls: "text-sky-200 bg-sky-500/20" },
  rnb: { Icon: Heart, cls: "text-rose-200 bg-rose-500/20" },
  adlib_wide: { Icon: Sparkles, cls: "text-emerald-200 bg-emerald-500/20" },
};
const PresetIcon = ({ id }) => {
  const { Icon, cls } = PRESET_ICONS[id] || PRESET_ICONS.none;
  return <span className={`w-6 h-6 rounded-md inline-flex items-center justify-center shrink-0 ${cls}`} data-testid={`fx-preset-icon-${id}`}><Icon className="w-3.5 h-3.5" /></span>;
};

export const fxIsActive = (track) => fxActive(track?.fx);

const sl = "py-2.5 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:bg-white [&_[role=slider]]:border-fuchsia-400 [&>span:first-child]:h-1.5 [&>span:first-child]:bg-white/10 [&>span:first-child>span]:bg-fuchsia-500";

function Knob({ label, value, min, max, step = 1, fmt, onChange, onCommit, testid, disabled }) {
  return (
    <div className={disabled ? "opacity-40" : ""}>
      <div className="flex justify-between text-[10px] text-gray-400"><span>{label}</span><span className="tabular-nums text-white" data-testid={`${testid}-value`}>{fmt ? fmt(value) : value}</span></div>
      <Slider min={min} max={max} step={step} value={[value]} disabled={disabled} className={sl}
        onValueChange={([v]) => onChange(v)} onValueCommit={([v]) => onCommit(v)} data-testid={testid} aria-label={label} />
    </div>
  );
}

function Module({ title, on, onToggle, children, testid }) {
  return (
    <div className="rounded-xl border border-white/10 p-3" data-testid={`fx-module-${testid}`}>
      <button type="button" onClick={onToggle} className="flex items-center justify-between w-full" aria-pressed={on} data-testid={`fx-${testid}-toggle`}>
        <span className="text-[11px] font-bold uppercase tracking-widest text-gray-200">{title}</span>
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${on ? "bg-fuchsia-500 text-white" : "bg-white/10 text-gray-400"}`}>{on ? "ON" : "OFF"}</span>
      </button>
      <div className="mt-2 space-y-1">{children(!on)}</div>
    </div>
  );
}

export default function FxDrawer({ open, onClose, track, studioPro, previewLeft, onStartPreview, onChange, onCommit }) {
  if (!open || !track) return null;
  const fx = normalizeFx(track.fx);
  const locked = !studioPro && !(previewLeft > 0);
  const upd = (patch, commit) => { const next = { ...fx, ...patch }; onChange(next); if (commit) onCommit(next); };
  const mod = (k, patch, commit) => upd({ [k]: { ...fx[k], ...patch } }, commit);
  const knob = (k, f, props) => (
    <Knob {...props} disabled={props.disabled || locked} value={fx[k][f]} onChange={(v) => mod(k, { [f]: v })} onCommit={(v) => mod(k, { [f]: v }, true)} testid={`fx-${k}-${f}`} />
  );
  const live = (fxActive(fx) && !locked) || gateActive(fx);
  return (
    <aside className="fixed inset-x-0 bottom-0 z-50 bg-[#0f0b1a] border-t border-fuchsia-500/30 rounded-t-2xl max-h-[75vh] overflow-y-auto pb-6" data-testid="fx-drawer" role="dialog" aria-label="Vocal FX">
      <header className="sticky top-0 bg-[#0f0b1a] flex items-center gap-2 px-4 py-3 border-b border-white/10 z-10">
        <Crown className="w-4 h-4 text-amber-300" />
        <p className="text-[11px] uppercase tracking-widest font-black text-amber-200 flex-1">Vocal FX</p>
        <button type="button" onClick={() => upd({ bypass: !fx.bypass }, true)} aria-pressed={fx.bypass}
          className={`flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${fx.bypass ? "border-red-400 text-red-300" : live ? "border-emerald-400/60 text-emerald-300" : "border-white/15 text-gray-400"}`} data-testid="fx-bypass-btn">
          <Power className="w-3 h-3" />{fx.bypass ? "BYPASSED" : live ? "FX ON" : "FX OFF"}
        </button>
        <button type="button" onClick={onClose} className="text-gray-400 p-1" data-testid="fx-close-btn" aria-label="Close FX"><X className="w-4 h-4" /></button>
      </header>
      {!studioPro && (
        <div className="mx-4 mt-3 rounded-xl border border-amber-300/30 bg-amber-400/10 p-3 text-xs" data-testid="fx-pro-lock">
          <p className="font-bold text-amber-200 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Pro effects (EQ, compression, reverb, delay, presets) need RGM Premium</p>
          <p className="text-gray-300 mt-1">{previewLeft > 0 ? `Preview playing · ${previewLeft}s left — not saved or exported.` : "Hear them for 15 seconds. Saving and Final Mix export need Premium Monthly, Yearly or Lifetime."}</p>
          <div className="flex gap-2 mt-2">
            {!(previewLeft > 0) && <button type="button" onClick={onStartPreview} className="px-3 py-1.5 rounded-full bg-white/10 font-bold" data-testid="fx-preview-btn">Preview 15s</button>}
            <Link to="/membership" className="px-3 py-1.5 rounded-full bg-amber-400 text-black font-black" data-testid="fx-upgrade-link">Upgrade</Link>
          </div>
        </div>
      )}
      <div className="px-4 pt-3">
        <Module title="Noise Gate · Free" on={fx.gate.on} onToggle={() => mod("gate", { on: !fx.gate.on }, true)} testid="gate">
          {(d) => (<>
            <Knob label="Strength (higher cuts more background noise)" value={fx.gate.amount} min={0} max={100} fmt={(v) => `${Math.round(v)}%`} disabled={d}
              onChange={(v) => mod("gate", { amount: v })} onCommit={(v) => mod("gate", { amount: v }, true)} testid="fx-gate-amount" />
            <p className="text-[10px] text-gray-500">Quiets room hiss and headphone bleed between your lines. Included for every artist.</p>
          </>)}
        </Module>
      </div>
      <div className={`px-4 pt-3 space-y-3 ${locked ? "pointer-events-none opacity-50" : ""}`}>
        <div className="grid grid-cols-2 gap-2" data-testid="fx-preset-list">
          {[{ id: "none", label: "No preset", blurb: "Use the modules below" }, ...FX_PRESETS].map((p) => {
            const art = PRESET_ART[p.id];
            const on = fx.preset === p.id;
            return (
              <button key={p.id} type="button" onClick={() => upd({ preset: p.id, bypass: false }, true)} data-testid={`fx-preset-${p.id}`} aria-pressed={on}
                className={`relative text-left rounded-xl overflow-hidden border aspect-[4/3] transition-transform active:scale-[0.97] ${on ? "border-amber-300 ring-2 ring-amber-300/60" : "border-white/10"}`}>
                {art ? <img src={art.img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" data-testid={`fx-preset-art-${p.id}`} />
                  : <span className="absolute inset-0 bg-gradient-to-br from-[#1a1326] to-black flex items-center justify-center"><PresetIcon id="none" /></span>}
                <span className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <span className="absolute left-2 right-2 bottom-1.5">
                  <span className="block text-[12px] font-black text-white leading-tight">{art?.name || p.label}</span>
                  <span className="block text-[9px] text-amber-200/90 font-bold uppercase tracking-wider">{art ? p.label : "Manual"}</span>
                  <span className="block text-[9px] text-gray-300 leading-tight line-clamp-2">{p.blurb}</span>
                </span>
                {on && <span className="absolute top-1.5 right-1.5 text-[9px] font-black bg-amber-300 text-black rounded px-1.5" data-testid={`fx-preset-active-${p.id}`}>ON</span>}
              </button>
            );
          })}
        </div>
        <Knob label="Preset intensity" value={fx.intensity} min={0} max={100} fmt={(v) => `${Math.round(v)}%`} disabled={fx.preset === "none" || locked}
          onChange={(v) => upd({ intensity: v })} onCommit={(v) => upd({ intensity: v }, true)} testid="fx-intensity" />
        <Module title="Vocal EQ" on={fx.eq.on} onToggle={() => mod("eq", { on: !fx.eq.on }, true)} testid="eq">
          {(d) => ["low", "mid", "high"].map((f) => <div key={f}>{knob("eq", f, { label: f[0].toUpperCase() + f.slice(1), min: -12, max: 12, step: 0.5, fmt: (v) => `${v > 0 ? "+" : ""}${v} dB`, disabled: d })}</div>)}
        </Module>
        <Module title="Compression" on={fx.comp.on} onToggle={() => mod("comp", { on: !fx.comp.on }, true)} testid="comp">
          {(d) => knob("comp", "amount", { label: "Amount", min: 0, max: 100, fmt: (v) => `${Math.round(v)}%`, disabled: d })}
        </Module>
        <Module title="Reverb" on={fx.reverb.on} onToggle={() => mod("reverb", { on: !fx.reverb.on }, true)} testid="reverb">
          {(d) => (<>{knob("reverb", "mix", { label: "Wet / dry", min: 0, max: 100, fmt: (v) => `${Math.round(v)}%`, disabled: d })}
            {knob("reverb", "decay", { label: "Decay", min: 0.3, max: 6, step: 0.1, fmt: (v) => `${v.toFixed(1)} s`, disabled: d })}</>)}
        </Module>
        <Module title="Delay" on={fx.delay.on} onToggle={() => mod("delay", { on: !fx.delay.on }, true)} testid="delay">
          {(d) => (<>{knob("delay", "time_ms", { label: "Time", min: 40, max: 1200, step: 10, fmt: (v) => `${Math.round(v)} ms`, disabled: d })}
            {knob("delay", "feedback", { label: "Feedback", min: 0, max: 85, fmt: (v) => `${Math.round(v)}%`, disabled: d })}
            {knob("delay", "mix", { label: "Wet / dry", min: 0, max: 100, fmt: (v) => `${Math.round(v)}%`, disabled: d })}</>)}
        </Module>
        <Module title="Stereo width" on={fx.width.on} onToggle={() => mod("width", { on: !fx.width.on }, true)} testid="width">
          {(d) => knob("width", "amount", { label: "Width (mono-safe)", min: 0, max: 100, fmt: (v) => `${Math.round(v)}%`, disabled: d })}
        </Module>
      </div>
    </aside>
  );
}
